/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React from 'react'
import { hot } from 'react-hot-loader/root'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/loader.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/grid.css'
import './styles.scss'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { browserCheck } from '@ta-interaktiv/browsercheck'
import Loadable from 'react-loadable'
import { AnnualComparison } from 'components/annualComparison'
import { TempStripes } from 'components/tempStripes'
import { GlobalStripes } from 'components/globalStripes'
import { MonthlyAnomaly } from 'components/monthlyAnomaly'
import { GlobalCo2 } from 'components/globalCo2'
import FlipBook from 'components/FlipBook'
import SwissMap from 'components/MapDisplay/SwissMap'
import WorldMap from 'components/MapDisplay/WorldMap'

// TODO Set the correct article ID here
const ARTICLE_ID = '208146915534'
const isMobile = browserCheck.isMobile

// FETCH DATA
const SwissLoader = Loadable({
  loader: () => {
    try {
      const stationData = fetch(
        'https://ta-interaktiv.storage.googleapis.com/klima-dashboard/meteoschweiz/anomaly_timeseries.json'
      ).then(x => x.json())
      return Promise.all([stationData])
    } catch (e) {
      console.log('ERR', e)
    }
  },
  loading: () => (
    <div className='ui text container load-wrapper'>
      <div className='ui segment'>
        <div className='ui active dimmer'>
          <div className='ui text loader'>Daten laden…</div>
        </div>
      </div>
    </div>
  ),
  render([stationData], props) {
    return (
      <>
        <div className='column'>
          <AnnualComparison
            data={stationData} // data={require('../../data/klima_dashboard_meteoschweiz_sma.json')}
            lastMonth='2026-08'
            title='Temperaturverlauf im Vergleich zu den Vorjahren'
            subtitle='Monatsmittel der Temperaturen in Grad Celsius in der Schweiz seit 1864, Referenzzeitraum: 1991-2020'
            lineColors={{
              current: '#d5211e',
              avg: '#FFFFFF',
              highlight: '#f5db5e',
              past: '#FFFFFF'
            }}
          />
        </div>

        <div className='column'>
          <TempStripes
            data={stationData} // data={require('../../data/klima_dashboard_meteoschweiz_sma.json')}
            lastMonth='2026-08'
            title='Ab Ende der 80er-Jahre wurde es wärmer in der Schweiz'
            subtitle='Abweichung des Monatsmittels der Temperatur in der Schweiz seit 1864, Referenzzeitraum: 1991-2020'
          />
        </div>
      </>
    )
  }
})

const GlobalLoader = Loadable({
  loader: () => {
    try {
      const stationData = fetch(
        'https://storage.googleapis.com/ta-interaktiv/klima-dashboard/noaa/anomaly_timeseries.json' //  'https://interaktiv.tagesanzeiger.ch/2020/daten-zum-klimawandel-in-der-schweiz/monthly_temperature_era5.json'
      ).then(x => x.json())
      return Promise.all([stationData])
    } catch (e) {
      console.log('ERR', e)
    }
  },
  loading: () => (
    <div className='ui text container load-wrapper'>
      <div className='ui segment'>
        <div className='ui active dimmer'>
          <div className='ui text loader'>Daten laden…</div>
        </div>
      </div>
    </div>
  ),
  render([stationData], props) {
    // console.log('stationData:', stationData)
    return (
      <>
        <div className='column'>
          <MonthlyAnomaly
            data={stationData} // data={require('../../data/monthly_temperature_era5.json')}
            lastMonth='2026-08'
            title='Temperatur-Abweichung vom langjährigen Mittel'
            subtitle='Monatliche durchschnittliche Anomalien der globalen Oberflächenlufttemperatur, Referenzzeitraum: 1901-2000'
            lineColors={{
              current: '#d5211e',
              avg: '#FFFFFF',
              highlight: '#f5db5e',
              past: '#FFFFFF'
            }}
          />
        </div>

        <div className='column'>
          <GlobalStripes
            data={stationData} // data={require('../../data/monthly_temperature_era5.json')}
            lastMonth='2026-08'
            title='Ab Anfang der 80er-Jahre wurde es wärmer auf&nbsp;der&nbsp;Welt'
            subtitle='Abweichung des Monatsmittels der globalen Temperatur (Land- und Meeresoberfläche) seit 1880, Referenzzeitraum: 1901-2000'
          />
        </div>
      </>
    )
  }
})

export class AppComponent extends React.Component<Props, State> {
  constructor(props) {
    super(props)
    this.state = {}
  }

  render() {
    return (
      <div className='index'>
        <Masthead articleId={ARTICLE_ID} inverted />
        <ThemeSwitch />

        <header className='ui vertical  very fitted inverted segment'>
          <div className='ui container'>
            <div className='ui header'>
              <h1>Die neusten Daten zum Klimawandel</h1>
              <h2>
                Wir zeigen jeden Monat mit Grafiken und Karten, wie sich die
                wichtigsten Kennzahlen des Klimas in der Schweiz und auf der
                Welt entwickeln.
              </h2>
            </div>
          </div>
        </header>

        <div className='ui vertical inverted segment'>
          <div className='ui text container'>
            <div className='ta metainfo'>
              <div className='ta author'>
                <nobr>Mathias Lutz,</nobr> <nobr>Andreas Moor,</nobr>{' '}
                <nobr>Marc Brupbacher</nobr>
              </div>
              <div className='ta updated'>
                Aktualisiert am 15. September 2026
              </div>
            </div>
            <div className='ta text' style={{ marginTop: '3em' }}>
              <div className='icon-wrapper'>
                <img src={require('./images/swiss.svg')} />
              </div>
              <h2>Der August in der Schweiz</h2>
              <p>
                Die Augusttemperatur lag im landesweiten Mittel{' '}
                <nobr>
                  <span className='anomaly warm'>+3,5 °C</span>
                </nobr>{' '}
                über der Norm 1991–2020. Das ist der zweithöchste Wert seit
                Messbeginn 1864. Der bisher wärmste August stammt aus dem Jahr
                2003. Die höchsten positiven Abweichungen zur Referenzperiode
                wurden in den Regionen Mittelland, Jura sowie Nord- und
                Mittelbünden verzeichnet. An 16 Messstationen erreichte die
                Monatsmitteltemperatur einen neuen August-Höchstwert für die
                letzten 90 Jahre.
              </p>
              <p>
                Die monatlichen Niederschlagsmengen lagen im August zwischen 43%
                und 179% des langjährigen Mittels. Ein Grossteil der
                Messstationen verzeichneten zwischen 60% und 110% der Norm.
              </p>
              <div className='source'>Quelle: Meteoschweiz</div>
            </div>
          </div>

          <div className='ui container'>
            <div className='ui equal width grid stackable'>
              <div className='equal width row'>
                <SwissLoader />
              </div>
              <div className='equal width row'>
                <div className='column'>
                  <h3>Monatliche Durchschnittstemperatur seit 1961</h3>
                  <h5>
                    Abweichung der Monatsmitteltemperatur von der Norm
                    1991–2020, 1 km-Raster
                  </h5>
                  <div style={{ marginTop: '1em' }}>
                    <FlipBook
                      render={(width, progress, setProgress, started) => (
                        <SwissMap
                          mode='tanom'
                          width={width}
                          progress={progress}
                          setProgress={setProgress}
                          started={started}
                          endDate={{
                            month: 8,
                            year: 2026
                          }}
                        />
                      )}
                    />
                  </div>
                </div>
                <div className='column'>
                  <h3>Monatlicher Niederschlag seit 1961</h3>
                  <h5>
                    Niederschlagssumme in % der Normperiode 1991–2020,
                    1 km-Raster
                  </h5>
                  <div style={{ marginTop: '1em' }}>
                    <FlipBook
                      render={(width, progress, setProgress, started) => (
                        <SwissMap
                          mode='ranom'
                          width={width}
                          progress={progress}
                          setProgress={setProgress}
                          started={started}
                          endDate={{
                            month: 8,
                            year: 2026
                          }}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='ui vertical inverted segment dark'>
          <div className='ui text container'>
            <div className='ta text'>
              <div className='icon-wrapper'>
                <img src={require('./images/global.svg')} />
              </div>
              <h2>Der August global</h2>
              <p>
                Die globale Land- und Meeresoberflächentemperatur lag im August
                um{' '}
                <nobr>
                  <span className='anomaly warm'>+1,32 °C</span>
                </nobr>{' '}
                über dem langjährigen Mittel und markiert den wärmsten August
                seit Beginn der globalen Aufzeichnungen im Jahr 1850. Somit
                wurden die zehn Höchstwerte für August allesamt seit 2016
                registriert. Weltweit lag die Landoberflächentemperatur im
                August durchschnittlich <nobr>+1,84 °C</nobr> über der Norm. Die
                globalen Ozeane verzeichneten mit <nobr>+1,08 °C</nobr> die
                höchste jemals gemessene Temperaturabweichung für einen Monat
                seit Beginn der Aufzeichnungen.
              </p>
              <div className='source'>Quelle: NOAA Global Climate Report</div>
            </div>
          </div>

          <div className='ui container'>
            <div className='ui equal width grid stackable'>
              <div className='equal width row'>
                <GlobalLoader />
              </div>
              <div className='equal width row'>
                <div className='column'>
                  <h3>Temperatur-Abweichung vom langjährigen Mittel</h3>
                  <h5>Referenzzeitraum: 1981–2010</h5>
                  <div style={{ marginTop: '1em' }}>
                    <FlipBook
                      render={(width, progress, setProgress, started) => (
                        <WorldMap
                          width={width}
                          progress={progress}
                          setProgress={setProgress}
                          started={started}
                          endDate={{
                            month: 8,
                            year: 2026
                          }}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
              <div className='equal width row'>
                <div className='column'>
                  <GlobalCo2
                    data={require('../../data/co2-concentration-long-term.json')}
                    lastMonth='2025-12'
                    title='CO₂-Konzentration in der Atmosphäre'
                    subtitle='Globale durchschnittliche Konzentration von Kohlendioxid (CO₂), gemessen in Teilen pro Millionstel (ppm). Vor 1958 gemessen anhand konservierter Luftproben aus Eiskernen, danach von der Messstation in Mauna Loa.'
                    lineColors={{
                      current: '#d5211e',
                      avg: '#FFFFFF',
                      highlight: '#f5db5e',
                      past: '#FFFFFF'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <Footer
          inverted
          hidePortfolioTeaser
          articleId={ARTICLE_ID}
          questions={[
            'Wie verständlich sind die Grafiken?',
            'Wie gefällt Ihnen die Gestaltung?'
          ]}
          credits={[
            [
              'Konzept und Umsetzung',
              '<nobr>Andreas Moor,</nobr> <nobr>Marc Brupbacher,</nobr> <nobr>Mathias Lutz</nobr>'
            ],
            [
              'Inspiration',
              '<a href="https://www.meteostats.ch/">meteostats.ch</a>'
            ]
          ]}
        />
      </div>
    )
  }
}

export default hot('AppComponent')
